import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import {
  Container,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import InfoIcon from '@mui/icons-material/Info';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

let socketInstance = null;

const theme = {
  primary: '#B43361',
  secondary: '#F3D4D4',
  background: '#FFFFFF',
  text: '#000000',
};

// Debounce utility function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const TriviaGameMaster = () => {
  const { pin } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState({
    players: [], // Initialize players as empty array
    currentQuestionIndex: 0,
    gameEnded: false,
    winners: [],
    question: null,
  });
  const [isLoading, setIsLoading] = useState(false); // For general actions
  const [isPlayersLoading, setIsPlayersLoading] = useState(false); // For player list updates
  const [error, setError] = useState('');

  const isAdmin = true; // Replace with actual admin check

  useEffect(() => {
    if (!isAdmin) return;

    if (!socketInstance) {
      socketInstance = io('https://bosscrowns-api-a228488a1e46.herokuapp.com', {
        autoConnect: false,
        query: { role: 'game-master' },
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
      });
    }

    socketInstance.connect();
    console.log(`GameMaster connected to socket, joined trivia session ${pin}`);

    socketInstance.emit('join-session', pin);

    const handleSessionState = ({ currentQuestionIndex, players, gameEnded, winners, question }) => {
      console.log(`Received session-state for ${pin}:`, { currentQuestionIndex, players, gameEnded, winners, question });
      setSession((prev) => {
        const updatedPlayers = players && players.length > 0 ? players.map(p => ({
          ...p,
          hasAnswered: p.hasAnswered ?? false, // Fallback for undefined hasAnswered
        })) : prev.players;
        console.log(`Updating session state, players:`, updatedPlayers);
        return {
          ...prev,
          currentQuestionIndex,
          players: updatedPlayers,
          gameEnded,
          winners: winners || [],
          question,
        };
      });
      setIsLoading(false);
      setIsPlayersLoading(false);
    };

    const handlePlayerJoined = (players) => {
      console.log(`Players joined in ${pin}:`, players);
      setSession((prev) => {
        const updatedPlayers = players && players.length > 0 ? players.map(p => ({
          ...p,
          hasAnswered: p.hasAnswered ?? false, // Fallback for undefined hasAnswered
        })) : prev.players;
        console.log(`Updating players from player-joined:`, updatedPlayers);
        return { ...prev, players: updatedPlayers };
      });
      setIsPlayersLoading(false);
    };

    const handlePlayerUpdated = (players) => {
      console.log(`Players updated in ${pin}:`, players);
      setSession((prev) => {
        const updatedPlayers = players && players.length > 0 ? players.map(p => ({
          ...p,
          hasAnswered: p.hasAnswered ?? false, // Fallback for undefined hasAnswered
        })) : prev.players;
        console.log(`Updating players from player-updated:`, updatedPlayers);
        return { ...prev, players: updatedPlayers };
      });
      setIsPlayersLoading(false);
    };

    const handleError = ({ message }) => {
      console.error(`Socket error in ${pin}: ${message}`);
      setError(message || 'An error occurred');
      setIsLoading(false);
      setIsPlayersLoading(false);
    };

    socketInstance.on('session-state', handleSessionState);
    socketInstance.on('player-joined', handlePlayerJoined);
    socketInstance.on('player-updated', handlePlayerUpdated);
    socketInstance.on('game-ended', handleSessionState);
    socketInstance.on('error', handleError);

    const fetchSession = async () => {
      try {
        setIsLoading(true);
        setIsPlayersLoading(true);
        const response = await fetch(`https://bosscrowns-api-a228488a1e46.herokuapp.com/bosscrowns/trivia-sessions/${pin}`);
        if (!response.ok) throw new Error('Trivia session not found');
        const data = await response.json();
        const gameTemplate = await fetch(
          `https://bosscrowns-api-a228488a1e46.herokuapp.com/bosscrowns/trivia/${data.gameTemplateId}`
        ).then((res) => res.json());
        console.log(`Fetched session data for ${pin}:`, data);
        setSession({
          ...data,
          players: (data.players || []).map(p => ({
            ...p,
            hasAnswered: p.hasAnswered ?? false, // Fallback for undefined hasAnswered
          })),
          question: gameTemplate.data.questions[data.currentQuestionIndex],
        });
      } catch (err) {
        console.error(`Error fetching trivia session ${pin}:`, err);
        setError('Failed to load session');
      } finally {
        setIsLoading(false);
        setIsPlayersLoading(false);
      }
    };
    fetchSession();

    return () => {
      console.log(`Cleaning up GameMaster socket for ${pin}`);
      if (socketInstance) {
        socketInstance.off('session-state', handleSessionState);
        socketInstance.off('player-joined', handlePlayerJoined);
        socketInstance.off('player-updated', handlePlayerUpdated);
        socketInstance.off('game-ended', handleSessionState);
        socketInstance.off('error', handleError);
        socketInstance.disconnect();
        socketInstance = null;
      }
    };
  }, [pin, isAdmin]);

  const nextQuestion = useCallback(
    debounce(() => {
      if (!isAdmin || session.gameEnded) return;
      setIsLoading(true);
      setIsPlayersLoading(true);
      socketInstance.emit('next-question', { pin });
      setTimeout(() => {
        setIsLoading(false);
        setIsPlayersLoading(false);
      }, 2000); // Timeout for loader
    }, 300),
    [pin, session.gameEnded, isAdmin]
  );

  const endSession = useCallback(() => {
    if (!isAdmin || session.gameEnded) return;
    setIsLoading(true);
    setIsPlayersLoading(true);
    socketInstance.emit('end-session', { pin });
    setTimeout(() => {
      setIsLoading(false);
      setIsPlayersLoading(false);
    }, 2000); // Timeout for loader
  }, [pin, session.gameEnded, isAdmin]);

  const startNewSession = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsPlayersLoading(true);
      socketInstance.emit('start-new-session', pin);
      await new Promise((resolve) => setTimeout(resolve, 500));
      navigate('/trivia/create');
    } catch (err) {
      console.error(`Error starting new trivia session for ${pin}:`, err);
      setError('Failed to start new session');
      setIsLoading(false);
      setIsPlayersLoading(false);
    }
  }, [pin, navigate]);

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 }, bgcolor: theme.background }}>
        <Box sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, textAlign: 'center' }}>
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: { xs: 1, sm: 2 } }}>
            <LockIcon sx={{ color: theme.primary, fontSize: { xs: '1.5rem', sm: '2rem' }, mr: 1 }} />
            <Typography variant="h6" sx={{ color: theme.text, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Access Denied: Only admins can access this page.
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 }, bgcolor: theme.background }}>
      <Box sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2, bgcolor: theme.background }}>
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <CasinoIcon sx={{ fontSize: { xs: 40, sm: 60 }, color: theme.primary, mb: 2 }} />
          <Typography variant="h4" sx={{ color: theme.text, fontWeight: 'bold', mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.8rem', sm: '2.5rem' } }}>
            Trivia Game Master Dashboard
          </Typography>
          {error && (
            <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {error}
            </Typography>
          )}
          <Typography variant="h6" sx={{ color: theme.text, mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Session PIN: {pin}
          </Typography>
          {session.gameEnded && session.winners.length > 0 && (
            <Typography variant="h4" sx={{ color: theme.primary, fontWeight: 'bold', mb: 3, fontSize: { xs: '2rem', sm: '3rem' } }}>
              Winner: {session.winners[0].name} - {session.winners[0].email} (Score: {session.winners[0].score})
            </Typography>
          )}
          {isLoading && <CircularProgress sx={{ mb: 2 }} />}
          {session.question && (
            <Box sx={{ width: '100%', maxWidth: 600, mb: 3, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ color: theme.text, mb: 1 }}>
                Question {session.currentQuestionIndex + 1}
              </Typography>
              <Typography variant="body1" sx={{ color: theme.text, mb: 2 }}>
                {session.question.question}
              </Typography>
              {session.question.answerType === 'multiple-choice' && (
                <List dense>
                  {session.question.options.map((option, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={`${idx + 1}. ${option}`}
                        primaryTypographyProps={{ color: theme.text }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {session.question.answerType === 'true-false' && (
                <List dense>
                  <ListItem>
                    <ListItemText primary="1. True" primaryTypographyProps={{ color: theme.text }} />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="2. False" primaryTypographyProps={{ color: theme.text }} />
                  </ListItem>
                </List>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="contained"
              onClick={nextQuestion}
              disabled={isLoading || session.gameEnded}
              startIcon={<PlayArrowIcon />}
              sx={{
                bgcolor: theme.primary,
                '&:hover': { bgcolor: theme.secondary },
                padding: { xs: '8px 16px', sm: '10px 20px' },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            >
              Next Question
            </Button>
            <Button
              variant="contained"
              onClick={endSession}
              disabled={isLoading || session.gameEnded}
              startIcon={<StopIcon />}
              sx={{
                bgcolor: theme.primary,
                '&:hover': { bgcolor: theme.secondary },
                padding: { xs: '8px 16px', sm: '10px 20px' },
                fontSize: { xs: '0.9rem', sm: '1rem' },
              }}
            >
              End Session
            </Button>
          </Box>
          <Box sx={{ width: '100%', maxWidth: 600, mb: 3 }}>
            <Typography variant="h6" sx={{ color: theme.text, mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              Players
            </Typography>
            {isPlayersLoading && !session.players.length ? (
              <CircularProgress size={24} sx={{ color: theme.primary, mb: 2 }} />
            ) : session.players.length > 0 ? (
              <List dense>
                {session.players.map((player, idx) => (
                  <ListItem key={player._id || idx}>
                    <ListItemIcon>
                      {player.hasAnswered ? (
                        <CheckCircleIcon sx={{ color: theme.primary }} />
                      ) : (
                        <InfoIcon sx={{ color: theme.primary }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={`${player.name} (Score: ${player.score})${player.hasAnswered ? ' - Answered' : ''}`}
                      secondary={player.email}
                      primaryTypographyProps={{ color: theme.text }}
                      secondaryTypographyProps={{ color: theme.text }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: theme.text }}>
                No players have joined yet.
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={startNewSession}
            disabled={isLoading}
            sx={{
              bgcolor: theme.primary,
              '&:hover': { bgcolor: theme.secondary },
              padding: { xs: '8px 16px', sm: '10px 20px' },
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            Start New Session
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default TriviaGameMaster;